import { ChangeDetectionStrategy, Component, effect, EventEmitter, input, Output, signal } from '@angular/core';
import { Wiki, Verifications } from '@local/client-contracts';
import { UiIconModel } from '@local/ui-infra';
import { UntilDestroy } from '@ngneat/until-destroy';
import { WikiCardVersionsService } from 'src/app/bar/services/wikis/wiki-card-versions.service';

export interface VersionListViewModel {
  version: number;
  modifiedBy: string;
  updatedTime: string;
  versionChangeLog: string;
  icon: UiIconModel;
}

@UntilDestroy()
@Component({
  selector: 'wiki-card-version-list',
  templateUrl: './wiki-card-version-list.component.html',
  styleUrl: './wiki-card-version-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WikiCardVersionListComponent {
  private readonly VERIFIED_ICON: string = './assets/verification/verified-light.svg';
  private readonly UNVERIFIED_ICON: string = './assets/verification/unverified-light.svg';

  versionListViewModel = signal<VersionListViewModel[]>(null);
  selectedCardVersion = signal<VersionListViewModel>(null);
  verificationIcon = signal<UiIconModel>(null);

  selectedIndex = input<number>(null);
  versions = input<Wiki.CardArchive[]>(null);
  verificationStatus = input<Verifications.VerificationStatus>(null);

  @Output() onSelectedVersion = new EventEmitter<number>();

  constructor(private cardVersionsService: WikiCardVersionsService) {
    effect(
      async () => {
        if (this.versions()) {
          await this.updateVersionDetails();
          this.selectInitialVersion();
        }
        if (this.verificationStatus()) {
          this.verificationIcon.set({
            type: 'img',
            value: { lightUrl: this.verificationStatus() === 'Verified' ? this.VERIFIED_ICON : this.UNVERIFIED_ICON },
          });
        }
      },
      { allowSignalWrites: true }
    );
  }

  private selectInitialVersion(): void {
    if (this.versions().length > 0) {
      this.selectedVersion(this.selectedIndex() || 0);
    }
  }

  selectedVersion(index: number): void {
    this.selectedCardVersion.set(this.versionListViewModel()[index]);
    this.onSelectedVersion.emit(index);
  }

  private async updateVersionDetails(): Promise<void> {
    const updatedVersions = await this.fetchUpdatedVersions();
    this.versionListViewModel.set(updatedVersions);
  }

  private async fetchUpdatedVersions(): Promise<VersionListViewModel[]> {
    const lengthVersions = this.versions().length - 1;
    return Promise.all(
      this.versions().map(async (version, index) => {
        const updateDetails = await this.cardVersionsService.setVersionOrderCard(version);
        return {
          ...version,
          ...updateDetails,
          modifiedBy: index !== lengthVersions ? `Updated by ${updateDetails.modifiedBy}` : `Created by ${updateDetails.modifiedBy}`,
        } as VersionListViewModel;
      })
    );
  }
}
